import * as yup from 'yup';

import { CountriesList } from '@/shared/constants/countries-list';
import { TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
// import { BaseDto, DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { ContactDto, ContactType } from '@/shared/lib/sj-orm/models/contact.dto';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';

import { FuturesDetails, FuturesGeneral } from '../../sj-orm/models/assets/categories';
import {
  FuturesBasicType,
  FuturesType,
  SellerType,
  TradeDirectionType,
} from '../../sj-orm/models/assets/enums';
import { CollectionName } from '../../sj-orm/types';

import { assetsDocumentsBuilder, AssetsDocumentsPartType } from './builders/documents';
import { assetsOwnershipBuilder, AssetsOwnershipPartType } from './builders/ownership';
import { assetsProfitabilityBuilder, AssetsProfitabilityPartType } from './builders/profitability';
import { assetsStatisticsBuilder, AssetsStatisticsPartType } from './builders/statistics';
import { baseAssetFields } from './fields';

export type Futures = FuturesFormGeneralType &
  FuturesFormDetailsType &
  Omit<AssetsOwnershipPartType, 'createdAt' | 'updatedAt'> &
  Omit<AssetsProfitabilityPartType, 'createdAt' | 'updatedAt'> &
  AssetsStatisticsPartType &
  Omit<AssetsDocumentsPartType, 'createdAt' | 'updatedAt'>;

export const FuturesList = [
  {
    label: 'forms_assets:futures.enums.type.deliveryBased',
    key: FuturesType.deliveryBased,
  },
  {
    label: 'forms_assets:futures.enums.type.cashSettled',
    key: FuturesType.cashSettled,
  },
];

const BasicAssetList = [
  {
    label: 'forms_assets:futures.enums.basicAssetType.commodity',
    key: FuturesBasicType.commodity,
  },
  {
    label: 'forms_assets:futures.enums.basicAssetType.equity',
    key: FuturesBasicType.equity,
  },
  {
    label: 'forms_assets:futures.enums.basicAssetType.currency',
    key: FuturesBasicType.currency,
  },
  {
    label: 'forms_assets:futures.enums.basicAssetType.cryptocurrency',
    key: FuturesBasicType.cryptocurrency,
  },
  {
    label: 'forms_assets:futures.enums.basicAssetType.bonds',
    key: FuturesBasicType.bonds,
  },
  {
    label: 'forms_assets:futures.enums.basicAssetType.index',
    key: FuturesBasicType.index,
  },
  {
    label: 'forms_assets:futures.enums.basicAssetType.interestRate',
    key: FuturesBasicType.interestRate,
  },
  {
    label: 'forms_assets:futures.enums.basicAssetType.futuresContract',
    key: FuturesBasicType.futuresContract,
  },
  {
    label: 'forms_assets:futures.enums.basicAssetType.other',
    key: FuturesBasicType.other,
  },
];

const TradeDirectionList = [
  {
    label: 'forms_assets:futures.enums.tradeDirection.long',
    key: TradeDirectionType.long,
  },
  {
    label: 'forms_assets:futures.enums.tradeDirection.short',
    key: TradeDirectionType.short,
  },
];

const SellerList = [
  {
    label: 'forms_assets:futures.enums.sellerType.legalEntity',
    key: SellerType.legalEntity,
  },
  {
    label: 'forms_assets:futures.enums.sellerType.person',
    key: SellerType.person,
  },
];

type FuturesHelpGeneral = {
  nickName: QuestionType.TEXT;
  country: QuestionType.SELECT_FROM_LIST;
  type: QuestionType.SELECT_FROM_LIST;
  amount: QuestionType.NUMBER;
  purchaseValue: QuestionType.ASSET_MARKET_VALUE_CALCULATOR;
  basicAssetName: QuestionType.TEXT;
  basicAssetType: QuestionType.SELECT_FROM_LIST;
  otherBasicAssetType?: QuestionType.TEXT;
  basicAssetAmount: QuestionType.NUMBER;
  broker: QuestionType.PICK_ARRAY_OF_DTO;
};

type FuturesHelpDetails = {
  isin: QuestionType.TEXT;
  ticker: QuestionType.TEXT;
  tradeDirection?: QuestionType.SELECT_FROM_LIST;
  expirationDate?: QuestionType.DATE_TIME;
  marginCollateral?: QuestionType.TEXT;
  exchangeName: QuestionType.TEXT;
  seller?: QuestionType.SELECT_FROM_LIST;
  legalEntityName?: QuestionType.TEXT;
  personFullName?: QuestionType.TEXT;
  brokerAccountNumber?: QuestionType.TEXT;
  dealReserves?: QuestionType.CHECKBOX;
  dealReservesData?: QuestionType.TEXT;
  strategy?: QuestionType.TEXT;
  attachments?: QuestionType.FILE_UPLOAD;
};

type FuturesFormGeneralType = TDtoWrapper<FuturesGeneral, FuturesHelpGeneral>;
type FuturesFormDetailsType = TDtoWrapper<FuturesDetails, FuturesHelpDetails>;

const FuturesFormGeneral: FuturesFormGeneralType = {
  nickName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.nickname.label',
    value: '',
    required: true,
    yupScheme: yup.string().required('forms_assets:common.inputs.nickname.errors.required'),
    colSpan: 2,
  },
  country: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.country.label',
    tooltip: 'forms_assets:common.inputs.country.tooltip',
    value: '',
    required: true,
    items: CountriesList,
    withSearch: true,
  },
  type: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:futures.inputs.type.label',
    tooltip: 'forms_assets:futures.inputs.type.tooltip',
    value: FuturesType.cashSettled,
    items: FuturesList,
    required: true,
  },
  amount: {
    type: QuestionType.NUMBER,
    label: 'forms_assets:futures.inputs.amount.label',
    required: true,
    value: 0,
  },
  purchaseValue: {
    type: QuestionType.ASSET_MARKET_VALUE_CALCULATOR,
    label: 'forms_assets:futures.inputs.purchaseValue.label',
    tooltip: 'forms_assets:futures.inputs.purchaseValue.tooltip',
    equivalentValueTooltip: 'forms_assets:common.inputs.equivalentValue.tooltip',
    value: 0,
    required: true,
  },
  basicAssetType: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:futures.inputs.basicAssetType.label',
    tooltip: 'forms_assets:futures.inputs.basicAssetType.tooltip',
    items: BasicAssetList,
    value: FuturesBasicType.commodity,
  },
  otherBasicAssetType: {
    type: QuestionType.TEXT,
    label: 'forms_assets:futures.inputs.otherBasicAssetType.label',
    value: '',
    showIf: {
      basicAssetType: {
        value: FuturesBasicType.other,
      },
    },
  },
  basicAssetName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:futures.inputs.basicAssetName.label',
    value: '',
  },
  basicAssetAmount: {
    type: QuestionType.NUMBER,
    label: 'forms_assets:futures.inputs.basicAssetAmount.label',
    value: 0,
  },
  broker: {
    type: QuestionType.PICK_ONE_OF_DTOS,
    label: 'forms_assets:futures.inputs.broker.label',
    tooltip: 'forms_assets:futures.inputs.broker.tooltip',
    colName: CollectionName.CONTACTS,
    value: [],
    filter: (contact) =>
      [ContactType.BROKER, ContactType.BANK].includes((contact as unknown as ContactDto).type),
    dtoType: [ContactType.BANK, ContactType.BROKER],
  },
};

const FuturesFormDetails: FuturesFormDetailsType = {
  isin: { type: QuestionType.TEXT, label: 'forms_assets:futures.inputs.isin.label', order: 2 },
  ticker: { type: QuestionType.TEXT, label: 'forms_assets:futures.inputs.ticker.label', order: 3 },
  tradeDirection: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:futures.inputs.tradeDirection.label',
    tooltip: 'forms_assets:futures.inputs.tradeDirection.tooltip',
    items: TradeDirectionList,
    order: 4,
  },
  expirationDate: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:futures.inputs.expirationDate.label',
    tooltip: 'forms_assets:futures.inputs.expirationDate.tooltip',
    order: 5,
  },
  marginCollateral: {
    type: QuestionType.TEXT,
    label: 'forms_assets:futures.inputs.marginCollateral.label',
    tooltip: 'forms_assets:futures.inputs.marginCollateral.tooltip',
    order: 6,
  },
  exchangeName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:futures.inputs.exchangeName.label',
    order: 7,
  },
  seller: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:futures.inputs.seller.label',
    items: SellerList,
    order: 8,
  },
  legalEntityName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:otcOptions.inputs.legalEntityName.label',
    showIf: {
      seller: {
        value: SellerType.legalEntity,
      },
    },
    order: 10,
  },
  personFullName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:otcOptions.inputs.personFullName.label',
    showIf: {
      seller: {
        value: SellerType.person,
      },
    },
    order: 11,
  },
  brokerAccountNumber: {
    type: QuestionType.TEXT,
    label: 'forms_assets:futures.inputs.brokerAccountNumber.label',
    order: 12,
  },
  dealReserves: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:futures.inputs.dealReserves.label',
    tooltip: 'forms_assets:futures.inputs.dealReserves.tooltip',
    order: 14,
  },
  dealReservesData: {
    type: QuestionType.TEXT,
    label: 'forms_assets:futures.inputs.dealReservesData.label',
    showIf: {
      dealReserves: {
        value: true,
      },
    },
    order: 15,
  },
  strategy: {
    type: QuestionType.TEXT,
    label: 'forms_assets:futures.inputs.strategy.label',
    tooltip: 'forms_assets:futures.inputs.strategy.tooltip',
    order: 16,
  },
  attachments: baseAssetFields.attachments,
};

const FuturesForm = [
  FuturesFormGeneral,
  FuturesFormDetails,
  assetsOwnershipBuilder().form,
  assetsProfitabilityBuilder([]),
  assetsStatisticsBuilder([
    'taxAgent',
    'taxAgentOther',
    'publicFundTaxAgent',
    'publicTaxAgentOther',
    'taxAgentWithBroker',
    'taxAgentWithBrokerOther',
    'legalTaxAgent',
    'legalTaxAgentOther',
    'specificsOfTaxation',
    'profitabilityFact',
    'profitabilityPlan',
  ]).form,
  assetsDocumentsBuilder(),
];
export { FuturesForm };
